import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchEvents, selectAllEvents, selectEventsStatus } from '../data/eventSlice';
import { baseURL } from '../services/APIService';
import { useBackendTranslation } from '../utils/useBackendTranslation';

const EventsGrid = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const eventsData = useSelector(selectAllEvents);
  const status = useSelector(selectEventsStatus);
  
  // Use the custom hook to translate events data
  const { translatedData: translatedEventsData, isLoading: translationLoading } = useBackendTranslation(
    eventsData, 
    { skipFields: ['id', 'date', 'time', 'imageData', 'hasImage', 'created_at', 'updated_at'] }
  );
  
  // Filter to only include upcoming events
  const upcomingEvents = useMemo(() => {
    if (!translatedEventsData) return [];
    
    const currentDate = new Date();
    // Remove time portion for accurate date comparison
    currentDate.setHours(0, 0, 0, 0);
    
    return translatedEventsData.filter(event => {
      // Parse the event date and set time to beginning of day for fair comparison
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0);
      // Keep only events that are today or in the future
      return eventDate >= currentDate;
    });
  }, [translatedEventsData]);

  useEffect(() => {
    if (status === 'idle') {
      dispatch(fetchEvents());
    }
  }, [status, dispatch]);

  return (
    <section className="py-16 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="text-sm font-semibold text-indigo-600 uppercase tracking-widest mb-4 block">
            {t('upcoming_events')}
          </span>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 bg-clip-text text-transparent">
              {t('events_title')}
            </span>
          </h2>
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-16 h-1 bg-gradient-to-r from-indigo-500 to-purple-500"></div>
            <div className="w-3 h-3 bg-indigo-500 rounded-full"></div>
            <div className="w-16 h-1 bg-gradient-to-r from-purple-500 to-blue-500"></div>
          </div>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed font-light">
            {t('events_description')}
          </p>
        </div>

        {status === 'loading' || translationLoading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-indigo-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {upcomingEvents.length > 0 ? (
              upcomingEvents.slice(0, 6).map((event) => (
                <div 
                  key={event.id}
                  className="group relative bg-white rounded-2xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                >
                  {/* Event Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={event.imageData ? event.imageData : (event.hasImage ? `${baseURL}/events/image/${event.id}` : '/assets/image1.jpeg')}
                      alt={event.title} 
                      className="w-full h-full object-cover transition-all duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                    
                    {/* Event Date & Time Badge - styled like a peeling corner sticker */}
                    <div className="absolute top-0 left-0 w-20 h-20">
                      <div className="absolute top-0 left-0 w-full h-full bg-white transform origin-top-left rotate-0 shadow-lg rounded-br-lg">
                        <div className="w-full h-full flex flex-col items-center justify-center text-center pt-1">
                          <span className="text-xl font-bold text-indigo-700">
                            {new Date(event.date).getDate()}
                          </span>
                          <span className="text-xs uppercase font-bold tracking-wide text-indigo-600">
                            {new Date(event.date).toLocaleString('default', { month: 'short' })}
                          </span>
                          <span className="text-xs text-gray-600 mt-1">
                            {event.time || (new Date(event.date).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}))}
                          </span>
                        </div>
                      </div>
                      {/* Peeling effect - small triangle */}
                      <div className="absolute bottom-0 right-0 w-3 h-3 bg-gray-300 transform rotate-45 translate-y-1 translate-x-1"></div>
                    </div>
                    
                    {/* Location Badge */}
                    <div className="absolute top-4 right-4 bg-indigo-600/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-white flex items-center">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      {event.location}
                    </div>
                  </div>
                  
                  {/* Event Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors duration-300">
                      {event.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-3">{event.description}</p>
                    
                    {/* Learn More Button */}
                    <Link 
                      to={`/events/${event.id}`}
                      className="inline-flex items-center font-semibold text-indigo-600 hover:text-indigo-800 transition-colors duration-300"
                    >
                      {t('learn_more')}
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full text-center py-12">
                <h3 className="text-xl text-gray-500">{t('no_upcoming_events')}</h3>
                <p className="mt-2 text-gray-400">{t('check_back_later')}</p>
              </div>
            )}
          </div>
        )}
        
        {/* View All Events Button */}
        <div className="text-center mt-12">
          <Link 
            to="/events"
            className="inline-flex items-center bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-3 px-8 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
          >
            {t('view_all_events')}
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default EventsGrid;
